import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import cn from 'classnames';

import type { TDisclaimer, IDocument } from '../models';
import { getDocument } from '../helpers';

export const Disclaimer: FC<TDisclaimer> = ({
    Block,
    Hr,
    Layout,
    Link,
    Markdown,
    Modal,
    Text,
    className,
    config,
    documents,
    isHr = true,
    styles,
    theme = 'black',
}) => {
    const [documentId, setDocument] = useState<number>(0);

    const handleCloseModal = () => setDocument(0);

    const themeHr = useMemo(() => {
        switch (theme) {
            case 'black':
                return 'lightInverted';
            case 'grey':
                return 'white';
            default:
                return theme;
        }
    }, [theme]);

    const isBlackTheme = theme === 'black';

    const textKind = isBlackTheme ? 'secondaryInverted' : 'secondary';
    const blockTheme = isBlackTheme ? 'lightInverted' : theme;
    const themeLink = isBlackTheme ? 'blue' : 'white';

    return (
        <>
            <Block
                className={className}
                notPadded
                styler={{
                    paddingTop: isHr ? 0 : config.pt,
                    paddingBottom: config.pb,
                }}
                theme={blockTheme}
            >
                <Layout>
                    {isHr && (
                        <Hr styler={{ marginBottom: config.mb, marginTop: 0 }} theme={themeHr} />
                    )}
                    {documents.map((doc: IDocument) => (
                        <Text
                            key={doc.id}
                            className={cn(styles.document, styles.external)}
                            styler={{
                                marginBottom: config.textMb,
                                marginRight: config.textMr,
                                marginTop: 0,
                            }}
                            view="primary-small"
                        >
                            <Link
                                border="solid"
                                className={cn(styles.external, {
                                    [styles.darkLink]: isBlackTheme,
                                })}
                                onClick={() => setDocument(doc.id)}
                                pseudo
                                theme={themeLink}
                            >
                                <Text kind={textKind} tag="span" view="primary-small">
                                    {doc.name}
                                </Text>
                            </Link>
                        </Text>
                    ))}
                </Layout>
            </Block>
            {!!documentId && (
                <Modal onClose={handleCloseModal} size="l">
                    <Markdown content={getDocument(documents, documentId)} />
                </Modal>
            )}
        </>
    );
};
